import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function SchoolPolicies(){
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();

  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ type: 'all', from: '', to: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({ title:'', description:'', item_type:'doc', link_url:'', policy_category:'' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedItemName, setDeletedItemName] = useState('');

  useEffect(()=>{ const sd = localStorage.getItem('schoolData'); if(!sd){ navigate('/schoollogin'); return; } setSchoolData(JSON.parse(sd)); setLoading(false); },[navigate]);
  useEffect(()=>{ if(schoolData?.id) fetchItems(); },[schoolData?.id, filters.type, filters.from, filters.to]);

  const fetchItems = async ()=>{
    let q = supabase.from('school_policies').select('*').eq('school_id', schoolData.id).order('created_at',{ascending:false});
    if(selectedBranchId) q = q.eq('branch_id', selectedBranchId);
    if(filters.type !== 'all') q = q.eq('item_type', filters.type);
    if(filters.from) q = q.gte('created_at', filters.from);
    if(filters.to) q = q.lte('created_at', filters.to + ' 23:59:59');
    const { data } = await q; setItems(data || []);
  };

  const resetForm = ()=> { setEditing(null); setFiles([]); setForm({ title:'', description:'', item_type:'doc', link_url:'', policy_category:'' }); };
  const openAdd = ()=> { resetForm(); setShowForm(true); };
  const openEdit = (it)=> { setEditing(it); setForm({ title: it.title, description: it.description||'', item_type: it.item_type, link_url: it.link_url||'', policy_category: it.policy_category||'' }); setShowForm(true); };
  
  const openAddWithCategory = (category) => {
    resetForm();
    setForm(prev => ({ ...prev, policy_category: category, title: category }));
    setShowForm(true);
  };

  const uploadFiles = async ()=>{
    if(!files || !files.length) return null;
    const uploaded = [];
    for(const f of files){
      const ext = f.name.split('.').pop();
      const key = `policies/${schoolData.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('student-images').upload(key, f);
      if(!error){ const { data: { publicUrl } } = supabase.storage.from('student-images').getPublicUrl(key); uploaded.push({ name:f.name, url:publicUrl, size:f.size }); }
    }
    return uploaded;
  };

  const saveItem = async ()=>{
    if(!form.title) return alert('Title required');
    let file_urls = await uploadFiles();
    const payload = { 
      school_id: schoolData.id, 
      branch_id: selectedBranchId || null,
      title: form.title,
      description: form.description || null,
      item_type: form.item_type,
      link_url: form.link_url || null,
      policy_category: form.policy_category || null,
      file_urls: file_urls && file_urls.length? file_urls : (editing?.file_urls || null)
    };
    if(editing){ 
      const { error } = await supabase
        .from('school_policies')
        .update({ 
          ...payload,
          updated_at: new Date().toISOString()
        })
        .eq('id', editing.id); 
      if(error) return alert('Failed to save changes: ' + error.message); 
    }
    else { 
      // Auto-publish new items on save
      const { error } = await supabase
        .from('school_policies')
        .insert({ 
          ...payload,
          is_published: true,
          published_at: new Date().toISOString()
        }); 
      if(error) return alert('Failed to save item: ' + error.message); 
    }
    await fetchItems(); setShowForm(false); resetForm();
  };

  const setPublish = async (it, pub)=>{
    const { error } = await supabase
      .from('school_policies')
      .update({ 
        is_published: pub, 
        published_at: pub ? new Date().toISOString() : null 
      })
      .eq('id', it.id);
    
    if (error) {
      alert('Failed to update publish status: ' + error.message);
      return;
    }
    fetchItems();
  };
  
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    const itemName = itemToDelete.title;
    const { error } = await supabase.from('school_policies').delete().eq('id', itemToDelete.id);
    
    if (error) {
      alert('Failed to delete item: ' + error.message);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      return;
    }
    
    setShowDeleteConfirm(false);
    setDeletedItemName(itemName);
    setShowDeleteSuccess(true);
    await fetchItems();
    
    // Auto-close success popup after 3 seconds
    setTimeout(() => {
      setShowDeleteSuccess(false);
      setDeletedItemName('');
    }, 3000);
  };

  const handleLogout = ()=> navigate('/schoollogin');
  if(loading) return <div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>;

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content" style={{ padding: '2rem', paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
        <div
          className="content-header"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            marginBottom: '1.5rem',
            padding: '1.75rem 2rem',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            gap: '1.25rem'
          }}
        >
          <div style={{ maxWidth: '720px' }}>
            <h1 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.9rem', color: '#1f2937', fontWeight: 700 }}>
              📘 School Policies / Handbook
            </h1>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.98rem' }}>
              Add/Edit documents, media and links; publish for students/parents
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: '640px',
              margin: '0 auto'
            }}
          >
            <button 
              className="btn-primary" 
              onClick={openAdd} 
              style={{ 
                minWidth: '150px', 
                padding: '0.75rem 1.5rem', 
                whiteSpace: 'nowrap',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              + Add Item
            </button>
          </div>
        </div>

        <div className="toolbar meals-toolbar tight" style={{ marginBottom: '1.5rem', padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div className="form-row single-line">
            <div className="form-group"><label>Filter</label><select value={filters.type} onChange={(e)=> setFilters({...filters, type:e.target.value})}><option value="all">All</option><option value="doc">Docs</option><option value="media">Media</option><option value="link">Links</option></select></div>
            <div className="form-group"><label>From</label><input type="date" value={filters.from} onChange={(e)=> setFilters({...filters, from:e.target.value})} /></div>
            <div className="form-group"><label>To</label><input type="date" value={filters.to} onChange={(e)=> setFilters({...filters, to:e.target.value})} /></div>
          </div>
        </div>

        <div className="classes-content compact full-width" style={{ marginTop: '0', padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div className="classes-header compact-header"><h1>Items ({items.length})</h1></div>
          <div className="classes-table-container">
              <table className="classes-table"><thead><tr><th>Category</th><th>Title</th><th>Type</th><th>Description</th><th>Files/Links</th><th>Published</th><th>Date</th><th>Actions</th></tr></thead><tbody>
              {items.length === 0 ? (
                <tr><td colSpan="8" style={{textAlign:'center', padding:'2rem', color:'#6b7280'}}>No items found. Click "+ Add Item" to create one.</td></tr>
              ) : (
                items.map(it=> (
                <tr key={it.id}>
                  <td>
                    {it.policy_category ? (
                      <span className="policy-category-badge">{it.policy_category}</span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>General</span>
                    )}
                  </td>
                  <td><strong>{it.title}</strong></td>
                  <td><span className={`type-badge ${it.item_type}`}>{it.item_type.toUpperCase()}</span></td>
                  <td style={{maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{it.description || '-'}</td>
                  <td>
                    {it.item_type==='link' && it.link_url ? (
                      <a href={it.link_url} target="_blank" rel="noreferrer" className="link-preview">🔗 Link</a>
                    ) : (
                      it.file_urls && Array.isArray(it.file_urls) && it.file_urls.length > 0 ? (
                        <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                          {it.file_urls.map((f,i)=> (
                            <a key={i} href={f.url} target="_blank" rel="noreferrer" className="file-link" title={f.name}>
                              📄 {f.name.length > 20 ? f.name.substring(0, 20) + '...' : f.name}
                            </a>
                          ))}
                        </div>
                      ) : '-'
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${it.is_published ? 'published' : 'draft'}`}>
                      {it.is_published ? '✅ Published' : '📝 Draft'}
                    </span>
                  </td>
                  <td>{new Date(it.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                      <button className="icon-btn" title="View/Edit" onClick={()=> openEdit(it)}>👁️</button>
                      <button className="icon-btn" title="Edit" onClick={()=> openEdit(it)}>✏️</button>
                      <button className="icon-btn" title={it.is_published? 'Unpublish':'Publish'} onClick={()=> setPublish(it, !it.is_published)}>
                        {it.is_published? '🔕':'📣'}
                      </button>
                      <button className="icon-btn danger" title="Delete" onClick={()=> handleDeleteClick(it)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody></table>
          </div>
        </div>

        {showForm && (
          <div className="modal-backdrop" onClick={()=> { setShowForm(false); resetForm(); }}>
            <div className="modal-card small" onClick={(e)=> e.stopPropagation()}>
              <div className="modal-header"><h3>{editing? 'Edit Item':'Add Item'}</h3><button className="close-btn" onClick={()=> { setShowForm(false); resetForm(); }}>×</button></div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Policy Category</label>
                  <select 
                    value={form.policy_category} 
                    onChange={(e)=> {
                      const category = e.target.value;
                      setForm({...form, policy_category: category, title: category || form.title});
                    }}
                  >
                    <option value="">Select Category (Optional)</option>
                    <option value="Terms & Conditions">Terms & Conditions</option>
                    <option value="Privacy Policy, EULA, Terms of Condition">Privacy Policy, EULA, Terms of Condition</option>
                    <option value="General Policy">General Policy</option>
                    <option value="Student Handbook">Student Handbook</option>
                    <option value="Code of Conduct">Code of Conduct</option>
                  </select>
                </div>
                <div className="form-group"><label>Title *</label><input value={form.title} onChange={(e)=> setForm({...form, title:e.target.value})} /></div>
                <div className="form-row">
                  <div className="form-group"><label>Type</label><select value={form.item_type} onChange={(e)=> setForm({...form, item_type:e.target.value})}><option value="doc">Doc</option><option value="media">Media</option><option value="link">Link</option></select></div>
                  {form.item_type==='link' && <div className="form-group"><label>Link URL</label><input value={form.link_url} onChange={(e)=> setForm({...form, link_url:e.target.value})} placeholder="https://..." /></div>}
                </div>
                <div className="form-group"><label>Description</label><textarea rows="4" value={form.description} onChange={(e)=> setForm({...form, description:e.target.value})}></textarea></div>
                {form.item_type!=='link' && <div className="form-group"><label>Files (docs/media)</label><input type="file" multiple onChange={(e)=> setFiles(Array.from(e.target.files))} /></div>}
                <div className="form-actions sticky-actions"><button className="btn-primary" onClick={saveItem}>{editing? 'Save Changes':'Save Item'}</button><button className="btn-secondary" onClick={()=> { setShowForm(false); resetForm(); }}>Cancel</button></div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        {showDeleteConfirm && itemToDelete && (
          <div className="delete-popup-overlay" onClick={cancelDelete}>
            <div className="delete-popup" onClick={(e) => e.stopPropagation()}>
              <div className="delete-popup-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#FEE2E2"/>
                  <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M12 8V12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M12 16H12.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="delete-popup-title">Delete Policy Item?</h3>
              <p className="delete-popup-message">
                Are you sure you want to delete <strong>"{itemToDelete.title}"</strong>? This action cannot be undone.
              </p>
              <div className="delete-popup-buttons">
                <button className="delete-popup-cancel-btn" onClick={cancelDelete}>
                  Cancel
                </button>
                <button className="delete-popup-confirm-btn" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Success Popup */}
        {showDeleteSuccess && (
          <div className="success-popup-overlay" onClick={() => setShowDeleteSuccess(false)}>
            <div className="success-popup" onClick={(e) => e.stopPropagation()}>
              <div className="success-popup-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#D1FAE5"/>
                  <path d="M9 12L11 14L15 10" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="success-popup-title">Successfully Deleted!</h3>
              <p className="success-popup-message">
                <strong>"{deletedItemName}"</strong> has been deleted successfully.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SchoolPolicies;


